import { useEffect, useMemo, useState, useCallback } from 'react';

/* ---------- helpers ---------- */
function buildProxyChain(url) {
  const enc = encodeURIComponent(url);
  return [
    url, // try direct first
    `https://api.allorigins.win/raw?url=${enc}`,
    `https://r.jina.ai/http://${url.replace(/^https?:\/\//, '')}`,
  ];
}

/* ---------- small JSON fetcher with proxy fallbacks ---------- */
async function fetchJsonWithFallbacks(url, signal) {
  const chain = buildProxyChain(url);
  let lastErr = null;
  for (const u of chain) {
    try {
      const r = await fetch(u, { headers: { Accept: 'application/json' }, signal });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return await r.json();
    } catch (e) {
      lastErr = e;
      continue;
    }
  }
  throw lastErr || new Error('All fallbacks failed');
}

/* ---------- TradingView tape ---------- */
function TradingViewTape() {
  const [stocks, setStocks] = useState([
    { symbol: 'BSE', price: '19,845.32', change: '+0.45%', trend: 'up' },
    { symbol: 'NIFTY', price: '19,632.50', change: '+1.2%', trend: 'up' },
    { symbol: 'RELIANCE', price: '2,845.60', change: '-0.8%', trend: 'down' },
    { symbol: 'TCS', price: '3,412.25', change: '+2.1%', trend: 'up' },
    { symbol: 'HDFC', price: '1,678.90', change: '+0.3%', trend: 'up' },
    { symbol: 'ICICI', price: '945.55', change: '-0.6%', trend: 'down' },
    { symbol: 'HDFCBANK', price: '1,678.90', change: '+0.4%', trend: 'up' },
    { symbol: 'INFY', price: '1,545.60', change: '+1.8%', trend: 'up' },
    { symbol: 'WIPRO', price: '678.90', change: '+0.9%', trend: 'up' },
    { symbol: 'SBI', price: '645.30', change: '+1.1%', trend: 'up' },
    { symbol: 'AXISBANK', price: '1,125.60', change: '-0.4%', trend: 'down' },
    { symbol: 'KOTAK', price: '1,890.25', change: '+0.7%', trend: 'up' },
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setStocks(prevStocks => 
        prevStocks.map(stock => ({
          ...stock,
          price: (parseFloat(stock.price) + (Math.random() - 0.5) * 10).toFixed(2),
          change: ((Math.random() - 0.5) * 4).toFixed(2),
          trend: Math.random() > 0.5 ? 'up' : 'down'
        }))
      );
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="tradingview-widget-container TradingViewWidget">
      <div className="tradingview-widget-container__widget">
        <div className="stock-ticker">
          <div className="stock-track">
            {[...stocks, ...stocks].map((stock, index) => (
              <div key={index} className="stock-item">
                <span className="stock-symbol">{stock.symbol}</span>
                <span className="stock-price">₹{stock.price}</span>
                <span className={`stock-change ${stock.trend}`}>
                  {stock.change}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- RSS Scroller (with fallbacks) ---------- */
function RSSScroller({ url, width = "100%", height = "30", refreshMs = 5 * 60 * 1000, speed = 4 }) {
  const [items, setItems] = useState([
    { title: "📈 Sensex gains 300 points, banking stocks rally", link: "#" },
    { title: "💹 Tech stocks see strong buying interest", link: "#" },
    { title: "🏢 Market closes at record high", link: "#" },
    { title: "💰 Rupee strengthens against dollar", link: "#" },
    { title: "🚗 Auto sector stocks surge ahead", link: "#" },
    { title: "💻 IT stocks show positive momentum", link: "#" },
    { title: "📊 Nifty 50 crosses 19,600 mark", link: "#" },
    { title: "🏭 Manufacturing sector shows growth", link: "#" },
    { title: "⚡ Energy stocks gain on oil prices", link: "#" },
    { title: "🏦 Banking sector leads gains", link: "#" },
    { title: "📱 Telecom stocks see mixed trends", link: "#" },
    { title: "🛒 Retail sector stocks in focus", link: "#" },
  ]);

  useEffect(() => {
    const interval = setInterval(() => {
      setItems(prevItems => {
        const shuffled = [...prevItems];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
      });
    }, 10000); // Shuffle every 10 seconds

    return () => clearInterval(interval);
  }, []);

  const heightCSS = typeof height === "number" ? `${height}px` : height;
  const baseDuration = 40; // seconds for speed=1
  const duration = Math.max(15, baseDuration / (Number(speed) || 1)); // Slower for better readability

  return (
    <div
      className="rss-scroller"
      style={{
        width,
        height: heightCSS,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
      }}
      aria-label="RSS Headlines"
    >
      <div
        className="rss-track"
        style={{
          display: "flex",
          whiteSpace: "nowrap",
          animation: `rssMarquee ${duration}s linear infinite`,
          gap: "30px",
          paddingLeft: 15,
        }}
      >
        {[...items, ...items].map((item, idx) => (
          <a
            key={idx}
            href={item.link}
            className="rss-item"
            style={{ color: "inherit", textDecoration: "none", paddingRight: 30 }}
            title={item.title}
          >
            {item.title}
          </a>
        ))}
      </div>

      <style>{`


/* ===== NEWS TICKER ===== */
.rss-scroller {
  overflow: hidden;
  width: 100%;
  position: relative;
}

.rss-track {
  display: flex;
  width: max-content;
  gap: 40px;
  animation: newsMarquee 22s linear infinite; /* FAST */
  will-change: transform;
}

.rss-item {
  display: inline-block;
  white-space: nowrap;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;

  color: #f1f5f9;
  background: linear-gradient(145deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02));
  border: 1px solid rgba(255,255,255,0.08);
  backdrop-filter: blur(6px);
  transition: all 0.3s ease;
}

.rss-item:hover {
  color: #f6b83b;
  background: rgba(246,184,59,0.12);
  transform: translateY(-2px);
}

@keyframes newsMarquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

/* Pause on hover */
.rss-scroller:hover .rss-track {
  animation-play-state: paused;
}



/* ===== STOCK TICKER CONTAINER ===== */
.stock-ticker {
  overflow: hidden;
  width: 100%;
  position: relative;
  padding: 2px 0;
}

/* Moving row */
.stock-track {
  display: flex;
  width: max-content;
  animation: stockMarquee 40s linear infinite;
}

/* ===== INDIVIDUAL STOCK BOX ===== */
.stock-item {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-right: 18px;
  padding: 0px 16px;

  border-radius: 14px;
  background: linear-gradient(145deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02));
  border: 1px solid rgba(255,255,255,0.08);

  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);

  box-shadow:
    0 4px 12px rgba(0,0,0,0.25),
    inset 0 0 8px rgba(255,255,255,0.04);

  transition: transform 0.3s ease, box-shadow 0.3s ease;
  white-space: nowrap;
}

/* Hover pop effect */
.stock-item:hover {
  transform: translateY(-3px) scale(1.04);
  box-shadow:
    0 8px 20px rgba(0,0,0,0.35),
    0 0 10px rgba(246,184,59,0.4);
}

/* ===== TEXT STYLES ===== */
.stock-symbol {
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #f8fafc;
}

.stock-price {
  font-weight: 600;
  color: #38bdf8;
}

/* UP trend */
.stock-change.up {
  color: #22c55e;
  font-weight: 700;
}

/* DOWN trend */
.stock-change.down {
  color: #ef4444;
  font-weight: 700;
}

/* Arrow indicators */
.stock-change.up::before {
  content: "▲";
  font-size: 10px;
  margin-right: 4px;
}

.stock-change.down::before {
  content: "▼";
  font-size: 10px;
  margin-right: 4px;
}

/* ===== SCROLL ANIMATION ===== */
@keyframes stockMarquee {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}












        @keyframes rssMarquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .rss-scroller {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          backdrop-filter: blur(10px);
        }
        .rss-track {
          display: flex;
          whiteSpace: nowrap;
          animation: rssMarquee ${duration}s linear infinite;
          gap: 30px;
          padding: 8px 0;
        }
        .rss-item {
          color: #f1f5f9;
          text-decoration: none;
          transition: color 0.3s ease;
          font-size: 0.9rem;
          font-weight: 500;
          padding: 4px 8px;
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.05);
          white-space: nowrap;
        }
        .rss-item:hover {
          color: #f6b83b;
          background: rgba(246, 184, 59, 0.1);
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  );
}

/* ---------- Local date/time (browser time zone) ---------- */
function useGeoClock(timeZoneOverride) {
  const [now, setNow] = useState(new Date());
  const { timeZone: sysTZ } = Intl.DateTimeFormat().resolvedOptions();
  const timeZone = timeZoneOverride || sysTZ;
  const locale = typeof navigator !== 'undefined' ? navigator.language : 'en-IN';

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const dateStr = now.toLocaleDateString(locale, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone,
  });
  const timeStr = now.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone,
    timeZoneName: "short",
  });

  const zoneCity = timeZone.split("/").pop()?.replace(/_/g, " ") || timeZone;
  return { dateStr, timeStr, timeZone, zoneCity };
}

/* ---------- News strip ---------- */
function NewsStrip() {
  const { dateStr, timeStr, zoneCity } = useGeoClock();

  return (
    <div className="table_main_news">
      <div className="breaking_news">Latest News</div>

      <div className="main_news">
        <RSSScroller
          url="https://hindi.news18.com/rss/khabar/nation/nation.xml"
          width="100%"
          height="30"
          refreshMs={5 * 60 * 1000}
          speed={0.02}
        />
      </div>

      <div
        className="clock-box"
        id="augutsyaclockbox"
        title={dateStr}
      >
        <span>
          {zoneCity} • {timeStr}
        </span>
      </div>
    </div>
  );
}
export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header id="header" className={`header widget-header header-style2 ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav">
        <div className="container-fluid">
          <div className="row">
            {/* Top widgets bar */}
            <div id="cover" className="col-12 cover">
              <div className="widgets-wrapper">
                <TradingViewTape />
                <NewsStrip />
              </div>
            </div>

            {/* Main header */}
            <div className="col-12 myzindex">
              <div className="header-wrap">
                {/* Logo area */}
                <div className="logo-area">
                  <div id="logo" className="logo">
                    <a href="https://www.augutsya.com/" title="Augutsya" className="logo-link">
                      <img
                        className="site-logo"
                        src="/atpwhite.png"
                        alt="Augutsya"
                        width="217"
                        height="35"
                      />
                      <div className="logo-badge">
                        <span className="badge-icon">⭐</span>
                        <span className="badge-text">Premium</span>
                      </div>
                    </a>
                  </div>
                </div>

                {/* Navigation */}
                <div className={`nav-wrap ${menuOpen ? "open" : ""}`}>
                  <nav id="mainnav" className="mainnav" role="navigation" onClick={() => setMenuOpen(false)}>
                    <ul id="menu-main" className="menu">
                      <li className="menu-item dropdown">
                        <select className="product-select" onChange={(e) => {
                          if (e.target.value) {
                            window.open(e.target.value, "_blank");
                          }
                        }}>
                          <option value="">Products</option>
                          <option value="https://beonandco.com">BEONANDCO</option>
                          <option value="https://aeonandco.com">AEONANDCO</option>
                          <option value="https://store.augutsya.com">AUGUTSYA STORE</option>
                        </select>
                      </li>

                      <li className="menu-item">
                        <a href="/" className="nav-link">
                          <span className="nav-text">HOME</span>
                          <span className="nav-indicator"></span>
                        </a>
                      </li>
                      
                      <li className="menu-item">
                        <a href="/services/" className="nav-link">
                          <span className="nav-text">SERVICES</span>
                          <span className="nav-indicator"></span>
                        </a>
                      </li>
                      
                      <li className="menu-item">
                        <a href="/utilities/" className="nav-link">
                          <span className="nav-text">UTILITIES</span>
                          <span className="nav-indicator"></span>
                        </a>
                      </li>
                      
                      <li className="menu-item">
                        <a href="/contact-us/" className="nav-link">
                          <span className="nav-text">CONTACT</span>
                          <span className="nav-indicator"></span>
                        </a>
                      </li>
                      
                      <li className="menu-item">
                        <a href="/about-us/" className="nav-link">
                          <span className="nav-text">ABOUT US</span>
                          <span className="nav-indicator"></span>
                        </a>
                      </li>

                      <li className="menu-item">
                        <a href="https://mail.augutsya.com:2096" target="_blank" className="login-btn">
                          <span className="btn-icon">🔐</span>
                          <span className="btn-text">Login</span>
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>

                {/* Mobile menu toggle */}
                <button 
                  className={`mobile-menu-toggle ${menuOpen ? 'active' : ''}`}
                  onClick={() => setMenuOpen(!menuOpen)}
                  aria-label="Toggle menu"
                >
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced inline styles */}
      <style>{`
:root {
  --header-bg: linear-gradient(180deg, #0b1324 0%, #0e1729 100%);
  --header-txt: #e5e7eb;
  --muted: #94a3b8;
  --accent: #3b82f6;
  --accent-2: #22d3ee;
  --border: rgba(148, 163, 184, 0.18);
  --gold: #f6b83b;
  --gold-2: #f59e0b;
}

/* Base header styles */
header.header {
  background: linear-gradient(135deg, rgba(11, 19, 36, 0.95) 0%, rgba(14, 23, 41, 0.95) 50%, rgba(31, 28, 44, 0.95) 100%);
  color: #f1f5f9;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px) saturate(120%);
  -webkit-backdrop-filter: blur(20px) saturate(120%);
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

header.header.scrolled {
  background: linear-gradient(135deg, rgba(11, 19, 36, 0.98) 0%, rgba(14, 23, 41, 0.98) 50%, rgba(31, 28, 44, 0.98) 100%);
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.15);
}

/* Widgets wrapper */
.widgets-wrapper {
  display: grid;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(180deg, rgba(246, 184, 59, 0.05) 0%, transparent 100%);
}

/* TradingView widget */
.TradingViewWidget { 
  height: 36px; 
  overflow: hidden; 
  border-radius: 12px; 
  background: rgba(15, 23, 42, 0.6); 
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

/* News strip */
.table_main_news { 
  display: grid; 
  grid-template-columns: auto 1fr auto; 
  align-items: center; 
  gap: 16px;
}

.breaking_news {
  padding: 8px 16px; 
  font-size: 12px; 
  font-weight: 700; 
  letter-spacing: .08em; 
  text-transform: uppercase;
  border: 1px solid rgba(246, 184, 59, 0.3); 
  border-radius: 999px; 
  color: white; 
  background: linear-gradient(135deg, var(--gold), var(--gold-2));
  box-shadow: 0 4px 12px rgba(246, 184, 59, 0.3);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
}

.main_news { 
  overflow: hidden; 
  border-radius: 12px; 
  border: 1px solid rgba(255, 255, 255, 0.1); 
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(10px);
}

.clock-box { 
  font-variant-numeric: tabular-nums; 
  font-size: 14px; 
  color: white; 
  padding: 8px 16px; 
  border: 1px solid rgba(255, 255, 255, 0.1); 
  border-radius: 12px; 
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(34, 211, 238, 0.2));
  backdrop-filter: blur(10px);
}

/* Header wrap */
.myzindex .header-wrap { 
  display: grid; 
  grid-template-columns: 1fr auto; 
  align-items: center; 
  gap: 24px; 
  padding: 20px 0;
}

/* Logo area */
.logo-area {
  display: flex;
  align-items: center;
}

#logo .logo-link {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

#logo .site-logo { 
  display: block; 
  height: 40px; 
  width: auto; 
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3)); 
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1); 
}

.logo-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(246, 184, 59, 0.2), rgba(246, 184, 59, 0.1));
  border: 1px solid rgba(246, 184, 59, 0.3);
  font-size: 11px;
  font-weight: 600;
  color: var(--gold-2);
  backdrop-filter: blur(8px);
  opacity: 0;
  transform: translateX(-10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

#logo .logo-link:hover .logo-badge {
  opacity: 1;
  transform: translateX(0);
}

#logo .logo-link:hover .site-logo { 
  transform: translateY(-2px) scale(1.05); 
}

/* Navigation */
.nav-wrap { 
  display: flex;
  align-items: center;
}

#mainnav .menu { 
  display: flex; 
  gap: 8px; 
  justify-content: flex-end; 
  align-items: center; 
  list-style: none; 
  margin: 0; 
  padding: 0; 
}

/* Navigation links */
.nav-link {
  position: relative; 
  display: inline-flex; 
  align-items: center;
  padding: 12px 16px; 
  font-weight: 600; 
  letter-spacing: .02em; 
  color: #f1f5f9;
  text-decoration: none; 
  border: 2px solid transparent; 
  border-radius: 12px; 
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.nav-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(246, 184, 59, 0.1), transparent);
  transition: left 0.5s ease;
}

.nav-link:hover::before {
  left: 100%;
}

.nav-link:hover {
  color: #f1f5f9; 
  border-color: rgba(246, 184, 59, 0.3); 
  background: rgba(246, 184, 59, 0.05);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(246, 184, 59, 0.2);
}

.nav-text {
  position: relative;
  z-index: 2;
}

.nav-indicator {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 2px;
  background: var(--gold);
  transition: all 0.3s ease;
  transform: translateX(-50%);
}

.nav-link:hover .nav-indicator {
  width: 80%;
}

/* Product select */
.product-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  padding: 12px 40px 12px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #f1f5f9;

  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(10px);

  cursor: pointer;
  min-width: 200px;

  background-image: url("data:image/svg+xml,%3Csvg fill='none' stroke='%23f6b83b' stroke-width='2' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 16px;

  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.product-select:hover {
  border-color: rgba(246, 184, 59, 0.4);
  background: rgba(15, 23, 42, 0.8);
  transform: translateY(-1px);
}

.product-select:focus {
  outline: none;
  border-color: var(--gold);
  box-shadow: 0 0 0 3px rgba(246, 184, 59, 0.2);
}

.product-select option {
  padding: 12px;
  font-size: 14px;
  background: #0f172a;
  color: #f1f5f9;
}

/* Login button */
.login-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 700;
  color: #000000;
  text-decoration: none;

  background: linear-gradient(135deg, var(--gold), var(--gold-2));
  border: none;
  border-radius: 12px;

  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(246, 184, 59, 0.3);
}

.login-btn:hover {
  background: linear-gradient(135deg, var(--gold-2), var(--gold));
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(246, 184, 59, 0.4);
}

.login-btn:active {
  transform: translateY(0);
  box-shadow: 0 4px 12px rgba(246, 184, 59, 0.3);
}

.login-btn:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(246, 184, 59, 0.3);
}

/* Mobile menu toggle */
.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.hamburger-line {
  width: 24px;
  height: 2px;
  background: #f1f5f9;
  border-radius: 2px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-menu-toggle:hover .hamburger-line {
  background: var(--gold);
}

.mobile-menu-toggle.active .hamburger-line:nth-child(1) {
  transform: rotate(45deg) translate(5px, 5px);
}

.mobile-menu-toggle.active .hamburger-line:nth-child(2) {
  opacity: 0;
}

.mobile-menu-toggle.active .hamburger-line:nth-child(3) {
  transform: rotate(-45deg) translate(7px, -6px);
}

/* Responsive design */
@media (max-width: 1024px) { 
  .myzindex .header-wrap { 
    grid-template-columns: 1fr auto; 
    gap: 16px;
  } 
  .nav-wrap { 
    grid-column: 1 / -1; 
  } 
  #mainnav .menu { 
    justify-content: center; 
    flex-wrap: wrap; 
    gap: 8px; 
  }
}

@media (max-width: 768px) {
  .widgets-wrapper {
    gap: 8px;
    padding: 8px 0;
  }
  
  .table_main_news { 
    grid-template-columns: 1fr; 
    gap: 8px; 
  }
  
  .clock-box { 
    justify-self: start; 
  }
  
  .myzindex .header-wrap { 
    grid-template-columns: 1fr auto; 
    gap: 12px; 
    padding: 16px 0;
  }
  
  #mainnav .menu { 
    gap: 4px; 
  }
  
  #logo .site-logo { 
    height: 32px; 
  }
  
  .logo-badge {
    display: none;
  }
  
  .nav-link {
    padding: 8px 12px;
    font-size: 14px;
  }
  
  .mobile-menu-toggle {
    display: flex;
  }
  
  .nav-wrap {
    position: fixed;
    top: 0;
    left: -100%;
    width: 80%;
    height: 100vh;
    background: linear-gradient(135deg, rgba(11, 19, 36, 0.98) 0%, rgba(14, 23, 41, 0.98) 100%);
    backdrop-filter: blur(20px);
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 999;
    padding: 80px 20px 20px;
    overflow-y: auto;
  }
  
  .nav-wrap.open {
    left: 0;
  }
  
  #mainnav .menu {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  
  .nav-link {
    justify-content: center;
    padding: 16px;
    border-radius: 12px;
  }
  
  .login-btn {
    justify-content: center;
    margin-top: 16px;
  }
}

/* Marquee animation */
@keyframes rssMarquee { 
  0% { transform: translateX(0); } 
  100% { transform: translateX(-50%); } 
}

.rss-item {
  color: #f1f5f9;
  text-decoration: none;
  transition: color 0.3s ease;
}

.rss-item:hover {
  color: var(--gold);
}
      `}</style>
    </header>
  );
}
